import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useSeo } from "@/lib/seo";

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="text-lg font-bold tracking-tight text-ink">{title}</h2>
    <div className="mt-2 space-y-2 text-sm leading-relaxed text-muted">{children}</div>
  </section>
);

export default function ReturnsPolicy() {
  useSeo({ title: "Returns Policy · Better Days", description: "Better Days returns, refunds and delivery policy for supplement orders.", path: "/returns-policy" });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-surface">
      <header className="border-b border-line bg-card/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-3.5">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/better-days-mark.png" alt="Better Days" className="h-6 w-auto" />
            <span className="text-[15px] font-bold tracking-tight text-ink">Better Days</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-muted hover:text-ink" data-testid="returns-back">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-12" data-testid="returns-policy-page">
        <h1 className="text-3xl font-bold tracking-tight text-ink sm:text-4xl">Returns Policy</h1>
        <p className="mt-6 text-sm leading-relaxed text-muted">
          We want you to feel confident when shopping with Better Days. If something isn’t right, we’re here to help.
        </p>

        <Section title="Returns">
          <p>If you’re not happy with your purchase, you can return it within 30 days of receiving your order. Items must be unused, in their original packaging, and in a condition suitable for resale.</p>
        </Section>

        <Section title="Supplements">
          <p>For safety reasons, we can only accept returns of supplements that are unopened and still sealed.</p>
        </Section>

        <Section title="Digital Products">
          <p>Digital items (such as app subscriptions or downloadable content) cannot be returned, but if you experience any issues, please contact us and we’ll make it right.</p>
        </Section>

        <Section title="Refunds">
          <p>Once your return is received and checked, we’ll process your refund to your original payment method. Refunds typically take 3–5 working days to appear.</p>
        </Section>

        <Section title="Exchanges">
          <p>If you’d prefer an exchange, just let us know — we’re happy to help.</p>
        </Section>

        <Section title="How to Start a Return">
          <p>Email us at <a href="mailto:support@betterdays.fit" className="font-medium text-brand hover:underline">support@betterdays.fit</a> with your order number and the item you’d like to return. We’ll send you the return instructions.</p>
        </Section>

        <Section title="Damaged or Incorrect Items">
          <p>If your order arrives damaged or incorrect, please contact us within 7 days and we’ll replace it immediately.</p>
        </Section>

        <div className="mt-12 border-t border-line pt-6">
          <Link to="/" className="text-sm font-semibold text-brand hover:underline">← Back to Better Days</Link>
        </div>
      </main>
    </div>
  );
}
